// frontend/src/utils/htmlExport.ts
import { OvertimeStats } from '../types';
import {
  formatTime,
  calculateFTEVacationDays,
} from './timeFormatting';
import {
  calculateSummaries,
  calculateDailyVacationUsage,
  hasOvertimePermission,
  getMonthKey,
  getWeekKey,
} from './summaryCalculations';
import { formatDateWithWeekday } from './dateUtils';

export interface HtmlExportData {
  overtimeStats: OvertimeStats[];
  employeeName: string;
  holidays: Record<string, string>;
}

/**
 * Generates an HTML file with the overtime statistics table and triggers download
 */
export const downloadHtml = (data: HtmlExportData): void => {
  const { overtimeStats, employeeName, holidays } = data;

  if (overtimeStats.length === 0) {
    return;
  }

  const reversedStats = [...overtimeStats].reverse();
  const { monthlySummaries, weeklySummaries } = calculateSummaries(reversedStats);

  const html = `<!DOCTYPE html>
<html lang="de">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${employeeName} - Überstundenstatistik</title>
  <style>
    * {
      box-sizing: border-box;
    }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
        'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
        sans-serif;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      margin: 0;
      padding: 20px;
      background-color: #f5f5f5;
    }

    .container {
      max-width: 1400px;
      margin: 0 auto;
      background-color: white;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      padding: 20px;
    }

    h1 {
      margin: 0 0 10px 0;
      color: #333;
      font-size: 24px;
    }

    .export-date {
      color: #666;
      font-size: 14px;
      margin-bottom: 20px;
    }

    .table-wrapper {
      overflow-x: auto;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 14px;
    }

    thead {
      background-color: #f8f9fa;
      position: sticky;
      top: 0;
      z-index: 10;
    }

    th {
      padding: 12px 8px;
      text-align: left;
      font-weight: 600;
      color: #333;
      border-bottom: 2px solid #dee2e6;
      white-space: nowrap;
    }

    td {
      padding: 8px;
      border-bottom: 1px solid #e9ecef;
      position: relative;
    }

    tr:hover {
      background-color: #f8f9fa;
    }

    .monthly-summary-row {
      background-color: #e3f2fd;
      font-weight: bold;
    }

    .monthly-summary-row:hover {
      background-color: #bbdefb;
    }

    .weekly-summary-row {
      background-color: #f5f5f5;
      font-weight: 600;
      font-size: 0.95em;
    }

    .weekly-summary-row:hover {
      background-color: #eeeeee;
    }

    .vacation-day::before {
      content: '🏖️';
      margin-right: 4px;
    }

    .sick-day::before {
      content: '🤒';
      margin-right: 4px;
    }

    .date-with-icon {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      position: relative;
    }

    .date-with-icon:hover .tooltip,
    td:hover .tooltip {
      opacity: 1;
    }

    .tooltip {
      position: absolute;
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%);
      background-color: #333;
      color: #fff;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 0.85em;
      white-space: nowrap;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.15s;
      margin-bottom: 4px;
      z-index: 1000;
    }

    small {
      display: block;
      margin-top: 2px;
    }

    @media print {
      body {
        background-color: white;
        padding: 0;
      }

      .container {
        box-shadow: none;
        padding: 0;
      }

      .tooltip {
        display: none;
      }
    }
  </style>
</head>
<body>
  <div class="container">
    <h1>${employeeName} - Überstundenstatistik</h1>
    <div class="export-date">Exportiert am: ${new Date().toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })}</div>

    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Datum</th>
            <th>Erwartete Stunden</th>
            <th>Gearbeitete Stunden</th>
            <th>Krankheit</th>
            <th>Genutzter Urlaub</th>
            <th>Verbleibender Urlaub</th>
            <th>Überstunden Tag</th>
            <th>Überstunden gesamt</th>
          </tr>
        </thead>
        <tbody>
          ${generateTableRows(reversedStats, monthlySummaries, weeklySummaries, holidays)}
        </tbody>
      </table>
    </div>
  </div>
</body>
</html>`;

  // Create blob and trigger download
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${employeeName.replace(/\s+/g, '_')}_overtime_statistics_${
    new Date().toISOString().split('T')[0]
  }.html`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

function generateTableRows(
  reversedStats: OvertimeStats[],
  monthlySummaries: Record<string, any>,
  weeklySummaries: Record<string, any>,
  holidays: Record<string, string>
): string {
  const rows: string[] = [];
  let currentMonth = '';
  let currentWeek = '';
  let isFirstRow = true;

  reversedStats.forEach((stat, index) => {
    const statDate = new Date(stat.date);
    const monthKey = getMonthKey(statDate);
    const weekKey = getWeekKey(statDate);

    if (isFirstRow) {
      // Add current month and week summaries
      rows.push(generateMonthlySummaryRow(monthKey, monthlySummaries[monthKey]));
      rows.push(generateWeeklySummaryRow(weekKey, weeklySummaries[weekKey]));
      currentMonth = monthKey;
      currentWeek = weekKey;
      isFirstRow = false;
    } else {
      // Check for period changes
      if (currentMonth !== monthKey) {
        rows.push(generateMonthlySummaryRow(monthKey, monthlySummaries[monthKey]));
        currentMonth = monthKey;
      }

      if (currentWeek !== weekKey) {
        rows.push(generateWeeklySummaryRow(weekKey, weeklySummaries[weekKey]));
        currentWeek = weekKey;
      }
    }

    // Add daily row
    const nextStat = index < reversedStats.length - 1 ? reversedStats[index + 1] : null;
    rows.push(generateDailyStatRow(stat, nextStat, holidays));
  });

  return rows.join('\n');
}

function generateMonthlySummaryRow(monthKey: string, monthStats: any): string {
  const monthName = new Date(monthKey + '-01').toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'long'
  });

  return `
    <tr class="monthly-summary-row">
      <td colspan="3" style="text-align: left; padding-left: 12px;">${monthName}</td>
      <td></td>
      <td colspan="2" style="position: relative;">
        Urlaub genutzt: ${formatTime(monthStats.vacationUsed)}
        <span class="tooltip">${calculateFTEVacationDays(monthStats.vacationUsed)} FTE</span>
      </td>
      <td colspan="2" style="position: relative;">
        Überstunden: ${formatTime(monthStats.overtimeChange)}
        <span class="tooltip">${calculateFTEVacationDays(monthStats.overtimeChange)} FTE</span>
      </td>
    </tr>
  `;
}

function generateWeeklySummaryRow(weekKey: string, weekStats: any): string {
  const weekNumber = parseInt(weekKey.split('-W')[1], 10);

  return `
    <tr class="weekly-summary-row">
      <td colspan="3" style="text-align: left; padding-left: 20px;">KW ${weekNumber}</td>
      <td></td>
      <td colspan="2" style="position: relative;">
        Urlaub: ${formatTime(weekStats.vacationUsed)}
        <span class="tooltip">${calculateFTEVacationDays(weekStats.vacationUsed)} FTE</span>
      </td>
      <td style="position: relative;">
        Erfasst: ${formatTime(weekStats.overtimeTracked)}
        <span class="tooltip">${calculateFTEVacationDays(weekStats.overtimeTracked)} FTE</span>
      </td>
      <td style="position: relative;">
        Gewährt: ${formatTime(weekStats.overtimeGranted)}
        <span class="tooltip">${calculateFTEVacationDays(weekStats.overtimeGranted)} FTE</span>
      </td>
    </tr>
  `;
}

function generateDailyStatRow(
  stat: OvertimeStats,
  nextStat: OvertimeStats | null,
  holidays: Record<string, string>
): string {
  const dailyUsedVacationMinutes = calculateDailyVacationUsage(stat, nextStat);
  const hasPermission = hasOvertimePermission(stat, nextStat);

  const statDate = new Date(stat.date);
  const dayOfWeek = statDate.getDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const hasWorkedOnWeekend = isWeekend && stat.totalMinutesWorked > 0;
  const holidayName = holidays[stat.date];
  const isHoliday = !!holidayName;

  const isUsingVacation = dailyUsedVacationMinutes > 0;
  const isSick = stat.sickMinutes > 0;

  const getIconTooltipText = () => {
    if (isUsingVacation && isSick) return 'Urlaub eingetragen & Arbeitsunfähigkeit';
    if (isUsingVacation) return 'Urlaub eingetragen';
    if (isSick) return 'Arbeitsunfähigkeit';
    return '';
  };

  const overtimeTooltip = `${calculateFTEVacationDays(stat.overOrUnderTimeMinutes)} FTE${
    !hasPermission ? ' - Keine Überstundengenehmigung' : ''
  }`;

  const dateClass = `${isUsingVacation ? 'vacation-day' : ''} ${isSick ? 'sick-day' : ''}`.trim();

  return `
    <tr style="color: ${isWeekend ? '#999' : 'inherit'};">
      <td class="${dateClass}" style="background-color: ${isHoliday ? '#ecf7ee' : 'transparent'}; font-weight: ${isHoliday ? 'bold' : 'normal'}; position: relative;">
        <span class="date-with-icon">
          ${formatDateWithWeekday(stat.date)}
          ${(isUsingVacation || isSick) ? `<span class="tooltip">${getIconTooltipText()}</span>` : ''}
        </span>
        ${isHoliday ? `<span class="tooltip">${holidayName}</span>` : ''}
      </td>
      <td>${formatTime(stat.expectedMinutesWorked)}</td>
      <td style="color: ${hasWorkedOnWeekend ? '#333' : 'inherit'};">${formatTime(stat.totalMinutesWorked)}</td>
      <td>
        ${formatTime(stat.sickMinutes)}
      </td>
      <td>
        <div>${formatTime(dailyUsedVacationMinutes)}</div>
        <small style="color: #666;">(${calculateFTEVacationDays(dailyUsedVacationMinutes)} FTE)</small>
      </td>
      <td>
        <div>${formatTime(stat.remainingVacationMinutes)}</div>
        <small style="color: #666;">(${calculateFTEVacationDays(stat.remainingVacationMinutes)} FTE)</small>
      </td>
      <td style="color: ${hasWorkedOnWeekend ? '#333' : 'inherit'}; position: relative;">
        ${formatTime(stat.overOrUnderTimeMinutes)}
        <span class="tooltip">${overtimeTooltip}</span>
      </td>
      <td style="color: ${hasWorkedOnWeekend ? '#333' : 'inherit'}; position: relative;">
        ${formatTime(stat.totalOverOrUndertime)}
        <span class="tooltip">${calculateFTEVacationDays(stat.totalOverOrUndertime)} FTE</span>
      </td>
    </tr>
  `;
}
